import { IonContent } from '@ionic/react';
import { AnimatePresence, useReducedMotion } from 'motion/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import CameraIcon from '@/assets/camera.svg?react';
import { UiTypography } from '@/components/ui/Typography';
import { amplitudeModule } from '@/modules/amplitude';
import { webCampaign } from '@/modules/webCampaign';
import { useOnboarding } from '@/pages/Onboarding/context';
import { SEND_PHOTOS_OPTIONS } from '@/pages/Onboarding/options/SendPhotos/SEND_PHOTOS_OPTIONS';
import { SendPhotosOptionsType } from '@/pages/Onboarding/options/SendPhotos/types';
import { OnbOptionType } from '@/pages/Onboarding/types';
import { nextStepTimeout } from '@/pages/Onboarding/utils/nextStepTimeout';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';

import { OwnAiTitle, OwnAiTitleHighlight } from '../../components/Title';
import { HARDCODED_CATEGORIES } from '../../constants';
import { useLayoutFormSelections } from '../../hooks/useLayoutFormSelections';

import {
  SendPhotosAnswerItemStyles,
  SendPhotosAnswersStyles,
  SendPhotosIconStyles,
  SendPhotosStyles,
} from './SendPhotos.styles';

import type { LayoutDataType } from '../../types';

export const ScreenSendPhotos: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'SendPhotos',
      });
    }
  }, []);

  const shouldAnimate = webCampaign.isAnimated();
  const [showElements, setShowElements] = React.useState<boolean>(true);

  const { wizard } = useOnboarding();

  const form = useFormContext<LayoutDataType>();

  const sendPhotos = form.watch('send_photos');

  const { ageGeneration, bodyShape, hairColor } = useLayoutFormSelections();

  const handleSelect = (
    option: OnbOptionType<LayoutDataType['send_photos']>,
  ) => {
    form.setValue('send_photos', option.id);
    userService.createOrUpdate({
      categories: HARDCODED_CATEGORIES,
      onboarding_data: {
        send_photos: option.title,
        first_categories: HARDCODED_CATEGORIES,
      },
    });

    if (shouldAnimate) {
      setTimeout(() => {
        if (shouldAnimate) {
          setShowElements(false);
        }
        nextStepTimeout(wizard.nextStep);
      }, 200);
    } else {
      nextStepTimeout(wizard.nextStep);
    }
  };

  return (
    <SendPhotosStyles
      background="dark-violet"
      bgImage={
        <img
          src={`https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/layout-own-ai-second/${ageGeneration}/${bodyShape}/${hairColor}/send_photos.jpg`}
          alt="Ai say"
        />
      }
    >
      <IonContent>
        <div>
          <SendPhotosIconStyles>
            <CameraIcon />
          </SendPhotosIconStyles>

          <OwnAiTitle animate={shouldAnimate} show={showElements}>
            Would you like to receive{' '}
            <OwnAiTitleHighlight>photos</OwnAiTitleHighlight>?
          </OwnAiTitle>

          <SendPhotosAnswersStyles>
            {SEND_PHOTOS_OPTIONS.map((option) => (
              <Option
                key={option.id}
                option={option}
                isSelected={sendPhotos === option.id}
                onClick={handleSelect}
                show={showElements}
                animate={shouldAnimate}
              />
            ))}
          </SendPhotosAnswersStyles>
        </div>
      </IonContent>
    </SendPhotosStyles>
  );
};

function Option({
  isSelected,
  onClick,
  option,
  show,
  animate,
}: {
  isSelected: boolean;
  onClick: (option: SendPhotosOptionsType[number]) => void;
  option: SendPhotosOptionsType[number];
  show: boolean;
  animate?: boolean;
}) {
  const shouldReduceMotion = useReducedMotion();
  const canAnimate = animate && !shouldReduceMotion;

  if (canAnimate) {
    const scale = isSelected ? 1.03 : 0.97;

    return (
      <AnimatePresence>
        {show && (
          <SendPhotosAnswerItemStyles
            isSelected={isSelected}
            onClick={() => onClick(option)}
            initial={{ x: -100, opacity: 0 }}
            animate={{ x: 0, opacity: 1, scale }}
            exit={{ x: 100, opacity: 0 }}
            answer={option.id}
          >
            <UiTypography variant="title-medium" className="answer-title">
              {option.title}
            </UiTypography>
          </SendPhotosAnswerItemStyles>
        )}
      </AnimatePresence>
    );
  }

  const scale = isSelected ? 1.03 : 1;

  return (
    <SendPhotosAnswerItemStyles
      key={option.id}
      answer={option.id}
      isSelected={isSelected}
      onClick={() => onClick(option)}
      initial={{
        scale,
      }}
      animate={{
        scale,
      }}
    >
      <UiTypography variant="title-medium" className="answer-title">
        {option.title}
      </UiTypography>
    </SendPhotosAnswerItemStyles>
  );
}
